import {
  NodeResizer,
  NodeToolbar,
  type Node,
  type NodeProps,
  useReactFlow,
  useOnSelectionChange,
} from '@xyflow/react';
import { memo, useCallback, useRef, useState } from 'react';
import { EditableLabel, EditableLabelRef } from '../../common/EditableLabel';
import { Box, Menu, MenuItem } from '@mui/material';
import { __SWEEP_DESIGN_TYPOGRAPHY_STYLES___, colors, IconButton } from '@sweep-io/sweep-design';
import { ColorPicker } from '../../common/color-picker/ColorPicker';
import { colorPickerColors } from '../../common/color-picker/colorPickerColors';
import { RfNodeSection, SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { SECTION_DEFAULT_COLOR, SECTION_MIN_HEIGHT, SECTION_MIN_WIDTH } from './section-consts';
import { getOpacityInHex } from '../../../lib/getOpacityInHex';
import classNames from 'classnames';
import { MoreActionsHorizontal, Textbox as TextboxIcon } from '@sweep-io/sweep-design/dist/icons';
import { snapPositionToGrid } from '../helpers/gridPositioningUtils';
import { useGetNodeAbsolutePosition } from '../helpers/useGetNodeAbsolutePosition';
import { SmallColorSwatchButton } from '../../common/color-picker/SmallColorSwatchButton';
import { CanvasNodeToolbar } from '../nodes/components/CanvasNodeToolbar';
import { Separator } from '../../common/rich-text-editor/toolbar-items/Separator';
import { useNodeResizing } from '../nodes/hooks/useNodeResizing';
import { resizeControlStyles } from '../nodes/resizeControlStyles';

const styles = {
  leftToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px',
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: '4px',
    backgroundColor: 'white',
    '&.editing': {
      borderColor: colors.blue[500],
    },
  },
  outerContainer: {
    display: 'flex',
    height: '100%',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    borderRadius: '4px',
    border: `1px solid ${colors.grey[300]}`,
    '&.drop-target': {
      borderColor: colors.blue[500],
    },
    cursor: 'move',
  },
} as const;

const SECTION_DEFAULT_OPACITY = getOpacityInHex(20);

const SectionNode = ({
  id: sectionId,
  selected,
  dragging,
  data: { name, color, isDropTarget, onDeleteClick, readonly, hideToolbar },
}: NodeProps<RfNodeSection>) => {
  const { updateNodeData } = useReactFlow();
  const { setNodes } = useReactFlow<SweepCanvasRfNode>();
  const { getNodeAbsolutePosition } = useGetNodeAbsolutePosition();

  const [isEditingName, setIsEditingName] = useState(false);
  const [multipleNodesSelected, setMultipleNodesSelected] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<(EventTarget & Element) | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<(EventTarget & Element) | null>(null);
  const [selectedColor, setSelectedColor] = useState(color ?? SECTION_DEFAULT_COLOR);

  const onSelectionChange = useCallback(
    ({ nodes }: { nodes: Node[] }) => {
      if (nodes.length > 1) {
        setMultipleNodesSelected(true);
      } else {
        setMultipleNodesSelected(false);
      }
    },
    [setMultipleNodesSelected],
  );

  useOnSelectionChange({ onChange: onSelectionChange });

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
  }, []);

  const handleColorChange = useCallback(
    (newColor: string) => {
      setSelectedColor(newColor);
      updateNodeData(sectionId, { color: newColor });
      handleClosePopover();
    },
    [sectionId, updateNodeData, handleClosePopover],
  );

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleUngroup = useCallback(() => {
    setNodes((nodes) => {
      // Update all child nodes to have no parent and set their positions to absolute (snapped to grid)
      const updatedNodes = nodes.map((node) => {
        if (node.parentId === sectionId) {
          // Get the absolute position of the child node
          const absolutePosition = getNodeAbsolutePosition(node.id);

          if (absolutePosition) {
            // Snap to grid
            const snappedPosition = snapPositionToGrid(absolutePosition, {
              withPaddingByNodeType: node.type,
            });

            return {
              ...node,
              parentId: undefined,
              position: snappedPosition,
            };
          }
        }
        return node;
      });

      // Remove the section node itself
      return updatedNodes.filter((node) => node.id !== sectionId);
    });
    handleCloseMenu();
  }, [setNodes, handleCloseMenu, sectionId, getNodeAbsolutePosition]);

  const handleDeleteSection = useCallback(
    (event: React.MouseEvent) => {
      onDeleteClick?.(event, sectionId);
      handleCloseMenu();
    },
    [handleCloseMenu, sectionId, onDeleteClick],
  );

  const editableLabelRef = useRef<EditableLabelRef>(null);

  const { resizing, onResizeStart, onResize, onResizeEnd } = useNodeResizing();

  const sectionNameLabelBgColor = isEditingName ? colors.white : color || colorPickerColors.Grey300;

  return (
    <>
      <NodeResizer
        isVisible={selected && !dragging && !multipleNodesSelected}
        handleClassName="section-node-handle"
        handleStyle={resizeControlStyles.handle}
        lineStyle={resizeControlStyles.line}
        minWidth={SECTION_MIN_WIDTH}
        minHeight={SECTION_MIN_HEIGHT}
        autoScale
        onResizeStart={onResizeStart}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
      />
      {!readonly && !hideToolbar && (
        <>
          <CanvasNodeToolbar
            className="nopan"
            isVisible={selected && !dragging && !multipleNodesSelected && !resizing}
            align="end"
          >
            <SmallColorSwatchButton
              color={selectedColor}
              onClick={handleButtonClick}
              pressed={Boolean(popoverAnchor)}
            />
            <Separator />

            <IconButton
              variant="squared"
              size="small"
              onClick={() => {
                editableLabelRef.current?.edit();
              }}
              pressed={isEditingName}
            >
              <TextboxIcon color={colors.grey[800]} />
            </IconButton>
            <Separator />
            <IconButton
              variant="squared"
              size="small"
              onClick={handleMenuClick}
              pressed={Boolean(menuAnchor)}
            >
              <MoreActionsHorizontal />
            </IconButton>
          </CanvasNodeToolbar>
        </>
      )}
      {!hideToolbar && (
        <NodeToolbar className="nopan" align="start" offset={4} isVisible={true}>
          <Box
            sx={{ ...styles.leftToolbar, backgroundColor: sectionNameLabelBgColor }}
            className={classNames({ editing: isEditingName })}
          >
            <EditableLabel
              ref={editableLabelRef}
              value={name}
              noBorder
              onValueConfirm={(value) => {
                const trimmedValue = value.trim();
                if (trimmedValue.length === 0) {
                  updateNodeData(sectionId, { name });
                } else {
                  updateNodeData(sectionId, { name: trimmedValue });
                }
              }}
              onIsEditingChange={setIsEditingName}
              inputSx={{
                ...__SWEEP_DESIGN_TYPOGRAPHY_STYLES___['body-medium'],
                color: colors.grey[800],
                height: '24px',
              }}
              editTrigger="doubleClick"
              minCharLength={0}
            />
          </Box>
        </NodeToolbar>
      )}
      <Box
        className={classNames({
          'drop-target': isDropTarget,
        })}
        sx={{
          ...styles.outerContainer,
          backgroundColor: `${selectedColor}${SECTION_DEFAULT_OPACITY}`,
        }}
        data-testid={`section-node-${sectionId}`}
        onDoubleClick={() => {
          editableLabelRef.current?.edit();
        }}
      />

      {popoverAnchor && (
        <ColorPicker
          anchorElement={popoverAnchor}
          closePopover={handleClosePopover}
          onColorChange={handleColorChange}
          selectedColor={selectedColor}
          singleRow
          position="top"
        />
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '172px',
          },
        }}
      >
        <MenuItem onClick={handleUngroup}>Ungroup</MenuItem>
        <MenuItem onClick={handleDeleteSection} sx={{ color: colors.feedback.destructive }}>
          Delete section
        </MenuItem>
      </Menu>
    </>
  );
};

const _memoSectionNode = memo(SectionNode);

export default _memoSectionNode;
